import React from "react";
import "../styles/process-visualization.css";
import "../styles/shared-ui.css";
import { getProcessInformation } from "../services/processService";
import {
  downloadUseCaseDiagram,
  getUseCaseDiagram,
  diagramErrorMessage,
} from "../services/useCaseDiagramService";
import {
  downloadActivityDiagram,
  getActivityDiagram,
} from "../services/activityDiagramService";
import SearchableDropdown from "../components/ui/SearchableDropdown";

const formatProcessOptionLabel = (process) => {
  const processName =
    process?.process_name || process?.name || "Unnamed process";
  const processVersion = process?.process_version ?? process?.version ?? 0;
  const companyName = process?.company?.name || "";

  return `${processName} (V${processVersion})| ${companyName}`;
};

// Each diagram's layout is fixed by the backend. `pixelRatio` is how many
// image pixels make one CSS pixel at 100% zoom (the activity PNG is 2x).
const DIAGRAMS = {
  usecase: {
    label: "Use-Case Diagram",
    fetch: (pid) => getUseCaseDiagram(pid, "svg"),
    download: downloadUseCaseDiagram,
    pixelRatio: 1,
    downloads: [
      { format: "svg", label: "Download SVG", title: "Download as SVG (sharp at any zoom)" },
      { format: "png", label: "Download PNG", title: "Download as PNG (2x resolution)" },
    ],
    // These mirror the diagram's colours.
    legend: [
      { role: "R", name: "Responsible (left)", color: "#1F5FAD" },
      { role: "A", name: "Accountable (right)", color: "#B3261E" },
    ],
    note: "Consulted (C) and Informed (I) roles and gateways are not drawn.",
  },
  activity: {
    label: "Activity Diagram",
    fetch: (pid) => getActivityDiagram(pid, "png"),
    download: downloadActivityDiagram,
    pixelRatio: 2,
    downloads: [
      { format: "report", label: "Download Report", title: "Download the Markdown report" },
      { format: "svg", label: "Download SVG", title: "Download as SVG (sharp at any zoom)" },
      { format: "png", label: "Download PNG", title: "Download as PNG (2x resolution)" },
    ],
    legend: [],
    note:
      "One swimlane per Responsible (R) job; tasks with no R job go in " +
      '"Unassigned". Gateways are drawn as fork/join (parallel) or ' +
      "decision/merge (exclusive, inclusive). Consulted (C) and Informed (I) " +
      "roles are not drawn.",
  },
};
const DIAGRAM_OPTIONS = [
  { value: "usecase", label: "Use Case" },
  { value: "activity", label: "Activity Diagram" },
];

const ZOOM_STEP = 0.15;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 3;
const clampZoom = (z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));

/**
 * Process UML Page
 * Shows a UML diagram of a process, generated by the backend from the process
 * ID: the use-case diagram (tasks as use cases, Responsible jobs on the left
 * and Accountable jobs on the right) or the activity diagram (task flow and
 * gateways in swimlanes per Responsible job).
 */
const ProcessUseCasePage = () => {
  const [labelToId, setLabelToId] = React.useState(new Map());
  const [selectedLabel, setSelectedLabel] = React.useState("");
  const [selectedId, setSelectedId] = React.useState(null);
  const [diagramType, setDiagramType] = React.useState("usecase");
  const diagram = DIAGRAMS[diagramType];

  const [imageUrl, setImageUrl] = React.useState("");
  const imageUrlRef = React.useRef("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [downloading, setDownloading] = React.useState(null);
  // Only the latest request may update the view when selections change quickly.
  const requestSeqRef = React.useRef(0);

  // Zoom is relative to the diagram's natural pixel size.
  const [zoom, setZoom] = React.useState(1);
  const [naturalWidth, setNaturalWidth] = React.useState(0);
  const viewportRef = React.useRef(null);
  // While true the diagram re-fits whenever its box changes width (window
  // resize, browser zoom, sidebar toggle). Zooming by hand turns it off so the
  // user's zoom is kept; "Fit" turns it back on.
  const autoFitRef = React.useRef(true);

  const revokeUrl = React.useCallback(() => {
    const current = imageUrlRef.current;
    if (current) {
      try {
        URL.revokeObjectURL(current);
      } catch (_) {}
    }
    imageUrlRef.current = "";
    setImageUrl("");
    setNaturalWidth(0);
  }, []);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await getProcessInformation();
        if (!mounted) return;
        const map = new Map();
        (Array.isArray(list) ? list : []).forEach((p) => {
          map.set(formatProcessOptionLabel(p), p.process_id || p.id);
        });
        setLabelToId(map);
      } catch (e) {
        if (mounted) setLabelToId(new Map());
        console.error("Failed to load processes for use-case diagram", e);
      }
    })();
    return () => {
      mounted = false;
      requestSeqRef.current += 1;
      revokeUrl();
    };
  }, [revokeUrl]);

  const processOptions = React.useMemo(
    () =>
      Array.from(labelToId.entries()).map(([label, id]) => ({
        label,
        value: String(id),
      })),
    [labelToId],
  );

  const loadDiagram = React.useCallback(
    async (pid, type) => {
      if (!pid) return;
      const seq = ++requestSeqRef.current;
      setLoading(true);
      setError("");
      revokeUrl();
      try {
        const blob = await DIAGRAMS[type].fetch(pid);
        if (seq !== requestSeqRef.current) return;
        const url = URL.createObjectURL(blob);
        imageUrlRef.current = url;
        setImageUrl(url);
      } catch (e) {
        if (seq !== requestSeqRef.current) return;
        console.error(`Failed to load ${type} diagram:`, e);
        setError(diagramErrorMessage(e));
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    },
    [revokeUrl],
  );

  const onProcessChange = (label) => {
    if (!label) {
      requestSeqRef.current += 1;
      setSelectedLabel("");
      setSelectedId(null);
      setError("");
      setLoading(false);
      revokeUrl();
      return;
    }
    setSelectedLabel(label);
    const pid = labelToId.get(String(label).trim()) ?? null;
    setSelectedId(pid);
    if (pid) loadDiagram(pid, diagramType);
  };

  const onDiagramTypeChange = (type) => {
    setDiagramType(type);
    if (selectedId) loadDiagram(selectedId, type);
  };

  const fitZoom = React.useCallback((width) => {
    const viewport = viewportRef.current;
    if (!width || !viewport) return 1;
    const available = viewport.clientWidth - 32; // viewport padding
    // Shrink wide diagrams to fit; never blow small ones up past 100%.
    return clampZoom(Math.min(1, available / width));
  }, []);

  const handleImageLoad = (e) => {
    // Width in CSS pixels at 100% zoom.
    const width = (e.currentTarget.naturalWidth || 0) / diagram.pixelRatio;
    autoFitRef.current = true;
    setNaturalWidth(width);
    setZoom(fitZoom(width));
  };

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !naturalWidth || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    // Only width matters for the fit; the box's height is fixed by the page.
    let lastWidth = viewport.clientWidth;
    let frame = 0;
    const ro = new ResizeObserver(() => {
      const width = viewport.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      if (!autoFitRef.current) return;
      // Next frame, so resizing the image never re-enters this observer's
      // own layout pass.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setZoom(fitZoom(naturalWidth)));
    });
    ro.observe(viewport);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [naturalWidth, fitZoom]);

  const zoomBy = (step) => {
    autoFitRef.current = false;
    setZoom((z) => clampZoom(z + step));
  };

  const fitToWidth = () => {
    autoFitRef.current = true;
    setZoom(fitZoom(naturalWidth));
  };

  const handleDownload = async (format) => {
    if (!selectedId) return;
    setDownloading(format);
    try {
      await diagram.download(selectedId, format);
    } catch (e) {
      console.error(`Failed to download ${diagramType} diagram (${format}):`, e);
      setError(diagramErrorMessage(e));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Process UML</h2>
            <p className="page-subtitle">
              UML diagrams of a process: the use-case diagram of its tasks and
              jobs, or the activity diagram of how the work flows
            </p>
          </div>
          {selectedId && imageUrl && (
            <div className="roles-toolbar">
              {diagram.downloads.map((d, i) => (
                <button
                  key={d.format}
                  className={
                    i === diagram.downloads.length - 1
                      ? "primary-btn"
                      : "secondary-btn"
                  }
                  onClick={() => handleDownload(d.format)}
                  disabled={!!downloading}
                  style={{ fontWeight: 600 }}
                  title={d.title}
                >
                  {downloading === d.format ? "Downloading…" : d.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="page-content whatif">
        <div className="role-card">
          <div className="section-title">Select Process and Diagram</div>
          <div className="form-grid">
            <div className="form-group">
              <label>Process</label>
              <SearchableDropdown
                id="usecase-process-search-select"
                className="input searchable-combobox"
                value={selectedLabel}
                options={processOptions}
                placeholder="Search process by name/company"
                clearOnFocus
                onChange={(label, option) =>
                  onProcessChange(option?.label ?? label)
                }
              />
            </div>
            <div className="form-group">
              <label htmlFor="uml-diagram-type">UML Diagram</label>
              <select
                id="uml-diagram-type"
                value={diagramType}
                onChange={(e) => onDiagramTypeChange(e.target.value)}
              >
                {DIAGRAM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ alignSelf: "end" }}>
              <button
                className="primary-btn"
                onClick={() => loadDiagram(selectedId, diagramType)}
                disabled={!selectedId || loading}
                title="Fetch the diagram again, e.g. after editing the process"
              >
                {loading ? "Loading…" : "Reload Diagram"}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              padding: "12px 16px",
              borderRadius: 8,
              background: "#fee2e2",
              color: "#991b1b",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            ⚠ {error}
          </div>
        )}

        {loading && !imageUrl && (
          <div className="role-card" style={{ marginTop: 12, color: "#6b7280" }}>
            Loading diagram…
          </div>
        )}

        {imageUrl && (
          <div className="role-card" style={{ marginTop: 12 }}>
            <div
              className="section-title"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <span>
                {diagram.label}
                {loading && (
                  <span style={{ marginLeft: 8, color: "#6b7280" }}>
                    (Loading...)
                  </span>
                )}
              </span>
              <span
                style={{
                  display: "flex",
                  gap: 16,
                  fontSize: 13,
                  fontWeight: 400,
                  color: "#4b5563",
                  flexWrap: "wrap",
                }}
              >
                {diagram.legend.map((item) => (
                  <span
                    key={item.role}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 18,
                        height: 3,
                        borderRadius: 2,
                        background: item.color,
                      }}
                    />
                    <strong style={{ color: item.color }}>{item.role}</strong>
                    {item.name}
                  </span>
                ))}
              </span>
            </div>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
              {diagram.note}
            </p>

            <div className="po-slider">
              {/* Large processes grow tall, so the diagram scrolls in place.
                  The low minimum keeps the box and the zoom bar under it on
                  screen together on short laptop screens. A stable scrollbar
                  gutter keeps the fit width from flipping as the vertical
                  scrollbar comes and goes. */}
              <div
                ref={viewportRef}
                style={{
                  height: "calc(100vh - 360px)",
                  minHeight: 320,
                  overflow: "auto",
                  scrollbarGutter: "stable",
                  background: "#ffffff",
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <img
                  src={imageUrl}
                  alt={`${diagram.label} for ${selectedLabel || "process"}`}
                  onLoad={handleImageLoad}
                  style={{
                    display: "block",
                    margin: "0 auto",
                    maxWidth: "none",
                    height: "auto",
                    width: naturalWidth ? naturalWidth * zoom : "100%",
                  }}
                />
              </div>
              <div className="po-zoom-controls">
                <button
                  type="button"
                  onClick={() => zoomBy(-ZOOM_STEP)}
                  title="Zoom Out"
                  aria-label="Zoom Out"
                >
                  −
                </button>
                <span className="po-zoom-level">{Math.round(zoom * 100)}%</span>
                <button
                  type="button"
                  onClick={() => zoomBy(ZOOM_STEP)}
                  title="Zoom In"
                  aria-label="Zoom In"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={fitToWidth}
                  title="Reset"
                  aria-label="Fit diagram to width"
                >
                  Fit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessUseCasePage;
